import { setDate, setMonth } from 'date-fns';
import { getDayOrdinal } from '@bridebook/toolbox/src/datepicker/get-day-ordinal';
import { IDatePickerUI, WeekDayUI } from '@bridebook/toolbox/src/datepicker/types';
import gazetteer, { CountryCodes, type Market } from '@bridebook/toolbox/src/gazetteer';
import getLocalisedSeason from '@bridebook/web/lib/datepicker/utils/get-localised-season-string';
import { getI18n } from '@bridebook/web/lib/i18n/getI18n';
import { format } from '@bridebook/web/lib/i18n/utils/get-date-fns-format';

export const getUndecidedDatePickerTitle = (): string =>
  getI18n().t('common:datePickerTitle.undecided');

const getTranslatedWeekday = (): Record<WeekDayUI, string> => ({
  'Mon-Thurs': getI18n().t('datepicker:options.label.monThurs'),
  Friday: getI18n().t('datepicker:options.label.friday'),
  Saturday: getI18n().t('datepicker:options.label.saturday'),
  Sunday: getI18n().t('datepicker:options.label.sunday'),
});

/**
 * Function `getDatepickerTitle` returns friendly date string from datepicker object
 *
 * @function getDatepickerTitle
 * @param {Object} - date picker Object
 * @param {Boolean?} - use don't remember text or empty when date is undecided/not filled
 * @param {boolean} - shortMonth, whether to use short month MMM in title
 * @param {boolean} - isUK whether user's countryCode is set to GB
 * @returns {String} - returns friendly date string
 */
const getDatepickerTitle = (
  datePickerUI: IDatePickerUI | null,
  dontRemember?: boolean,
  shortMonth: boolean = false,
  market?: Market,
): string => {
  if (!datePickerUI) return '';

  if (market == null) {
    market = gazetteer.getMarketByCountry(CountryCodes.GB);
  }

  const {
    year,
    yearUndecided,
    month,
    monthUndecided,
    season,
    day,
    weekDay: _weekDay,
    dayUndecided,
  } = datePickerUI;

  const i18n = getI18n();

  const monthString = month
    ? format(setMonth(new Date(), Number(month)), shortMonth ? 'MMM' : 'MMMM')
    : '';

  const localisedSeason = season ? getLocalisedSeason(season, market) : '';

  const monthSeasonWithYear = [monthString || localisedSeason, year].filter((v) => !!v).join(' ');

  const hasMonth = month || season;

  const weekDay = _weekDay ? getTranslatedWeekday()[_weekDay] : undefined;
  const hasDay = day || weekDay;

  if (
    dontRemember &&
    !(yearUndecided && monthUndecided && dayUndecided) &&
    (yearUndecided || monthUndecided || dayUndecided) &&
    !hasMonth &&
    !hasDay &&
    !year
  ) {
    return '';
  }

  if (yearUndecided && !hasMonth && !hasDay) {
    return dontRemember
      ? i18n.t('common:datePickerTitle.dontRemember')
      : getUndecidedDatePickerTitle();
  }

  if (yearUndecided && monthUndecided && weekDay) {
    return i18n.t('common:datePickerTitle.aWeekday', {
      weekDay,
    });
  }

  if (year && !month && !season && !day && !weekDay) {
    return `${year}`;
  }

  if ((year || yearUndecided) && weekDay) {
    return i18n.t('common:datePickerTitle.aWeekdayIn', {
      weekDay,
      monthSeasonWithYear,
    });
  }

  if ((year || yearUndecided) && (month || season) && !weekDay && !day) {
    return monthSeasonWithYear;
  }

  if (!year && month && day) {
    const fullDate = setDate(setMonth(new Date(), Number(month)), Number(day));
    return i18n.t('common:datePickerTitle.dayOfMonth', {
      day: format(fullDate, getDayOrdinal(i18n.language)),
      month: format(fullDate, 'MMMM'),
    });
  }

  if (year && month && day) {
    const fullDate = setDate(new Date(Number(year), Number(month), 1), Number(day));
    return format(fullDate, `${getDayOrdinal(i18n.language)} MMM yyyy`);
  }

  return '';
};

export default getDatepickerTitle;
